import { useRef } from 'react';
import { Button, Label, Popover } from 'react-aria-components';
import { Icon, Icons } from '../atoms/Icon';
import cx from 'classix';
import { AriaListBoxOptions, useComboBox, useFilter, useListBox, useOption } from 'react-aria';
import { ComboBoxStateOptions, Item, ListState, Node, useComboBoxState } from 'react-stately';

type Props = {
  items: InputDropdownItem[];
  type: 'default' | 'search' | 'tags';
  onSelectionChange?: ComboBoxStateOptions<InputDropdownItem>['onSelectionChange'];
  hintText?: string;
  label?: string;
  placeholder?: string;
  defaultSelectedKey?: string;
  // TODO: Rename and reconsider
  inputDisabled?: boolean;
} & (
  | {
      type: 'default';
      iconLeading?: keyof typeof Icons;
    }
  | {
      type: 'search';
    }
);

export type InputDropdownItem = {
  text: string;
  textValue: string | number;
};

const DropdownOption = ({
  item,
  state,
}: {
  item: Node<InputDropdownItem>;
  state: ListState<InputDropdownItem>;
}) => {
  const ref = useRef<HTMLLIElement>(null);
  const { optionProps, isSelected, isFocused } = useOption(
    {
      key: item.key,
    },
    state,
    ref,
  );

  return (
    <li
      {...optionProps}
      ref={ref}
      className={cx(
        'flex items-center rounded-md outline-none cursor-pointer px-2 py-2 mx-1 my-0.5 hover:bg-gray-50',
        isFocused && 'bg-gray-50',
      )}
    >
      {item.rendered}
      {isSelected && (
        <span className='ml-auto [&>svg>path]:stroke-brand-600'>
          <Icon type='check' size='20' />
        </span>
      )}
    </li>
  );
};

interface ListBoxProps extends AriaListBoxOptions<InputDropdownItem> {
  listBoxRef?: React.RefObject<HTMLUListElement>;
  state: ListState<InputDropdownItem>;
}

const ListBox = (props: ListBoxProps) => {
  const ref = useRef<HTMLUListElement>(null);
  const { listBoxRef = ref, state } = props;
  const { listBoxProps } = useListBox(props, state, listBoxRef);

  return (
    <ul {...listBoxProps} ref={listBoxRef}>
      {[...state.collection].map((item) => (
        <DropdownOption key={item.key} item={item} state={state} />
      ))}
    </ul>
  );
};

/** An <input /> style component that displays a dynamic list of items in a popover, filtered by the input text. */
export const InputDropdown = (props: Props) => {
  return (
    <ComboBox {...props} defaultItems={props.items}>
      {(item) => <Item key={item.textValue}>{item.text}</Item>}
    </ComboBox>
  );
};

const ComboBox = (props: Props & ComboBoxStateOptions<InputDropdownItem>) => {
  // Setup filter function and state.
  const { contains } = useFilter({ sensitivity: 'base' });
  const state = useComboBoxState({
    ...props,
    // 'items' must be excluded as providing it controls the items list & prevents the list from filtering when the user types.
    items: undefined,
    menuTrigger: 'manual',
    // Filters items by text input, unless this is treated as a select-only dropdown
    // TODO: It's pretty confusing to call this an "input dropdown" when you're unable to type. It's a form-relevant component, but maybe
    // there's a better name and it's worth separating them
    defaultFilter: props.inputDisabled ? undefined : contains,
    defaultSelectedKey: props.defaultSelectedKey,
  });

  const ref = useRef<HTMLDivElement>(null);

  // Setup refs and get props for child elements.
  const buttonRef = useRef(null);
  const inputRef = useRef(null);
  const listBoxRef = useRef(null);
  const popoverRef = useRef(null);

  const { buttonProps, inputProps, labelProps, listBoxProps } = useComboBox(
    {
      ...props,
      items: undefined,
      inputRef,
      buttonRef,
      listBoxRef,
      popoverRef,
    },
    state,
  );

  const hasLeadingIcon =
    (props.type === 'default' && props.iconLeading) || props.type !== 'default';

  return (
    <div className='flex flex-col gap-1'>
      {props.label && (
        <Label {...labelProps} className='text text-gray-500 font-medium'>
          {props.label}
        </Label>
      )}
      <div
        ref={ref}
        className='inline-flex items-center text-[16px] px-3 py-2 bg-white border border-gray-100 rounded-lg shadow-xs has-[:focus]:ring-brand-shadow-xs'
      >
        {hasLeadingIcon && (
          <div className='absolute left-7 [&>svg>path]:stroke-gray-400'>
            <Icon type={props.type === 'default' ? 'user-01' : 'search-lg'} size='20' />
          </div>
        )}
        <input
          {...inputProps}
          ref={inputRef}
          placeholder={props.placeholder ?? ''}
          readOnly={props.inputDisabled}
          className={cx(
            'outline-none w-full',
            hasLeadingIcon && 'pl-7',
            // The selection rule is a hack to visibly hide text selection, since normal
            // selection prevention does not work on input elements
            props.inputDisabled && 'cursor-pointer [&::selection]:bg-transparent',
          )}
          /** Clicking when input is enabled means we've focused the input for typing, so it should remain open
           * when clicking the input text. Otherwise, clicking toggles open & closed like when clicking the chevron */
          onClick={() => (props.inputDisabled ? state.toggle() : state.open())}
        />
        {/* TODO: Replace with a DS button once icons are in */}
        {props.type === 'default' && (
          <Button {...buttonProps} ref={buttonRef}>
            {/* Setting width and height is necessary to make the span encompass the icon and change mouse cursor to pointer for the button */}
            <span className='[&>svg>path]:stroke-gray-400 [&>svg]:cursor-pointer'>
              {state.isOpen ?
                <Icon type='chevron-up' size='20' />
              : <Icon type='chevron-down' size='20' />}
            </span>
          </Button>
        )}
      </div>
      {props.hintText && <div className='text-sm text-gray-500 mt-1'>{props.hintText}</div>}
      <Popover
        ref={popoverRef}
        triggerRef={inputRef}
        isOpen={state.isOpen}
        isNonModal
        style={{ width: ref.current?.getBoundingClientRect().width }}
        className='mt-[5px] overflow-y-auto bg-white shadow-lg border border-gray-100 rounded-lg outline-none focused:outline'
        // Adjusts the left position to align correctly with the input when used in a modal, while seemingly also not breaking the alignment in other contexts
        // Frankly I don't understand why this "works"; remove & tweak if we see alignment issues
        crossOffset={10}
      >
        <ListBox {...listBoxProps} state={state} />
      </Popover>
    </div>
  );
};

export default InputDropdown;
